/**
 * Media Session integration — lock-screen controls + media keys + Bluetooth.
 *
 * Wires `navigator.mediaSession.metadata` + `setActionHandler` for every
 * relevant action (play / pause / stop / seekbackward / seekforward /
 * seekto) so the OS-level transport controls drive the in-page audio
 * element. Safe no-op on browsers that don't expose `mediaSession`.
 *
 * The lib does the audio operations directly (`audioEl.play()`,
 * `audioEl.pause()`, `audioEl.currentTime = …`). Consumers pass callbacks
 * that fire AFTER each operation so the page UI / state can stay in sync.
 *
 * Build path:
 *   src   assets/js/src/single-article/media-session.ts
 *   ↓ esbuild (IIFE, target ES2020)
 *   dist  assets/dist/js/single-article/media-session.js  (attaches window.XOT.article.mediaSession)
 *
 * @since Phase 2 — JS modernization, bite 9
 */

const ARTIST_NAME      = 'XOTLIST';
const DEFAULT_BACK_SEC = 15;
const DEFAULT_FWD_SEC  = 30;

export interface MediaArtwork { src: string; sizes: string; type: string; }

export interface SessionMetadata {
    title:   string;
    album:   string;
    artist:  string;
    artwork: MediaArtwork[];
}

export interface SetupCallbacks {
    /** Fires after audioEl.play() — use to update UI / state flags. */
    onPlay?:  () => void;
    /** Fires after audioEl.pause(). */
    onPause?: () => void;
    /** Fires when the OS-level Stop action is invoked. The lib does NOT
        pause the audio here; the consumer's onStop is the canonical stop. */
    onStop?:  () => void;
    /** Fires after any seek (backward / forward / seekto). */
    onSeek?:  () => void;
    /** Optional metadata override. Defaults to {@link resolveArticleMetadata}. */
    metadata?: Partial<SessionMetadata>;
    /** Seconds to jump back on `seekbackward` if the OS doesn't pass an offset. */
    seekBackSec?: number;
    /** Seconds to jump forward on `seekforward` if no offset given. */
    seekForwardSec?: number;
}

/**
 * Read article-page metadata from the canonical DOM landmarks.
 *
 * Title:   `.xot-article__title` → `h1.entry-title` → `h1` → `document.title`
 * Hero img (for artwork): `.xot-article__hero img` → `.xot-article__featured img`
 *                       → `.post-thumbnail img` → first `<article>` img
 *                       → first `.entry-content` img
 * Album:   `document.title` with trailing " — Section" stripped
 */
export function resolveArticleMetadata(): SessionMetadata {
    const titleEl = document.querySelector('.xot-article__title, h1.entry-title, h1');
    const title   = titleEl ? (titleEl.textContent ?? '').trim() : document.title;
    const album   = document.title.replace(/ — .*$/, '');
    const hero    = document.querySelector<HTMLImageElement>(
        '.xot-article__hero img, .xot-article__featured img, .post-thumbnail img, article img:first-of-type, .entry-content img:first-of-type'
    );
    const artwork: MediaArtwork[] = hero?.src ? [
        { src: hero.src, sizes: '512x512', type: 'image/jpeg' },
        { src: hero.src, sizes: '192x192', type: 'image/jpeg' },
    ] : [];
    return { title, album, artist: ARTIST_NAME, artwork };
}

/**
 * Wire OS-level media controls to a given audio element.
 *
 * Returns `true` if mediaSession was found and the handlers were registered
 * (even if some action types threw — handler registration is wrapped in
 * try/catch so older browsers don't crash). Returns `false` when
 * mediaSession is unsupported or `audioEl` is missing.
 */
export function setupMediaSession(
    audioEl: HTMLAudioElement | null,
    cb: SetupCallbacks = {},
): boolean {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator) || !audioEl) {
        return false;
    }
    const ms     = (navigator as Navigator & { mediaSession: MediaSession }).mediaSession;
    const meta   = { ...resolveArticleMetadata(), ...(cb.metadata ?? {}) };
    const back   = cb.seekBackSec    ?? DEFAULT_BACK_SEC;
    const fwd    = cb.seekForwardSec ?? DEFAULT_FWD_SEC;

    try {
        ms.metadata = new MediaMetadata({
            title:   meta.title,
            artist:  meta.artist,
            album:   meta.album,
            artwork: meta.artwork,
        });
        ms.setActionHandler('play',  () => {
            audioEl.play();
            cb.onPlay?.();
        });
        ms.setActionHandler('pause', () => {
            audioEl.pause();
            cb.onPause?.();
        });
        ms.setActionHandler('stop',  () => { cb.onStop?.(); });
        ms.setActionHandler('seekbackward', (d) => {
            if (!audioEl.currentTime) return;
            const offset = ((d ?? {}) as { seekOffset?: number }).seekOffset ?? back;
            audioEl.currentTime = Math.max(0, audioEl.currentTime - offset);
            cb.onSeek?.();
        });
        ms.setActionHandler('seekforward',  (d) => {
            if (!audioEl.duration) return;
            const offset = ((d ?? {}) as { seekOffset?: number }).seekOffset ?? fwd;
            audioEl.currentTime = Math.min(audioEl.duration, audioEl.currentTime + offset);
            cb.onSeek?.();
        });
        ms.setActionHandler('seekto', (d) => {
            const seekTime = ((d ?? {}) as { seekTime?: number }).seekTime;
            if (typeof seekTime !== 'number' || !audioEl.duration) return;
            audioEl.currentTime = Math.max(0, Math.min(audioEl.duration, seekTime));
            cb.onSeek?.();
        });
        return true;
    } catch {
        // Older browsers reject unknown action types — silent partial success.
        return true;
    }
}

/* ── window.XOT.article.mediaSession attachment ────────────────────── */
interface XotArticleMediaSession {
    setup:                   typeof setupMediaSession;
    resolveArticleMetadata:  typeof resolveArticleMetadata;
}

declare global {
    interface Window {
        XOT?: {
            article?: {
                text?:         unknown;
                highlight?:    unknown;
                progress?:     unknown;
                share?:        unknown;
                audio?:        unknown;
                mediaSession?: XotArticleMediaSession;
            };
            [k: string]: unknown;
        };
    }
}

if (typeof window !== 'undefined') {
    const xot = (window.XOT ?? {}) as NonNullable<Window['XOT']>;
    const article = (xot.article ?? {}) as NonNullable<NonNullable<Window['XOT']>['article']>;
    article.mediaSession = {
        setup:                  setupMediaSession,
        resolveArticleMetadata,
    };
    xot.article = article;
    window.XOT  = xot;
}
